/**
 * Per-user "show inline help" switch. Persists to the account
 * (``PATCH /auth/profile`` → ``show_inline_help``) so the preference
 * follows the user across devices. Rendered as a labeled row inside the
 * admin account menu (see ``AdminUserMenu`` in ``AdminLayout``), next to
 * the theme control.
 */

import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { cx } from "@/lib/utils";
import { useAuthMe, useUpdateProfile } from "@/api/queries/auth";

export function HelpToggle(): JSX.Element | null {
  const { t } = useTranslation("admin");
  const me = useAuthMe();
  const update = useUpdateProfile();

  // Only for signed-in users (the toggle mutates their profile).
  if (!me.data) return null;
  const on = me.data.show_inline_help ?? true;

  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2">
      <span className="text-sm text-ink-2">{t("help.toggle.label")}</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={t("help.toggle.label")}
        title={t("help.toggle.hint")}
        onClick={() => update.mutate({ show_inline_help: !on })}
        disabled={update.isPending}
        data-testid="help-toggle"
        className={cx(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-60",
          on ? "bg-accent" : "bg-surface-3",
        )}
      >
        <span
          aria-hidden="true"
          className={cx(
            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
            on ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}
